import * as Sentry from '@sentry/nextjs';
import { ConnectedWallet } from '@privy-io/react-auth';
import { ConnectedStandardSolanaWallet } from '@privy-io/react-auth/solana';
import { toast } from 'sonner';
import {
  createWalletClient,
  custom,
  SwitchChainError,
  UserRejectedRequestError,
  type Address,
  type Chain,
  type WalletClient,
} from 'viem';
import { Config, SwitchChainNotSupportedError } from 'wagmi';
import { getAccount, getWalletClient } from 'wagmi/actions';
import { SwitchChainMutateAsync } from 'wagmi/query';

import { MatchaConnectedWallet } from '@/hooks/useMatchaWallets';

import { sleep } from '@/utils/sleep';

import {
  solana,
  CHAIN_IDS,
  NETWORK_NAME_PER_CHAIN_ID,
  SUPPORTED_CHAINS_BY_CHAIN_ID,
} from '../constants/chain';
import { logger } from '../lib/logger';
import { collectErrorMessages } from './errorChain';
import { trackError } from './errors';
import { config as wagmiConfig } from './wagmi';

// Patterns that indicate the user deliberately cancelled/rejected the transaction.
// Includes localized wallet messages observed in Sentry.
const CANCELLATION_PATTERNS = [
  'user rejected',
  'user denied',
  'rejected by user',
  'cancelled',
  'canceled',
  'operation cancelled',
  // Localized cancellation messages from wallets
  'membatalkan', // Indonesian
  'abgebrochen', // German
  'annulé', // French
  'отменен', // Russian (masculine)
  'отменена', // Russian (feminine)
];

/**
 * Checks if an error represents a user rejection (EIP-1193 code 4001).
 * Handles both proper Error instances and plain objects thrown by some wallets.
 * Walks the error cause chain to catch rejections wrapped by viem/Privy.
 */
export function isUserRejection(err: unknown): boolean {
  if (err instanceof UserRejectedRequestError) return true;

  if (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    (err as { code: unknown }).code === 4001
  )
    return true;

  const messages = collectErrorMessages(err);
  return messages.some((msg) => {
    const lower = msg.toLowerCase();
    return CANCELLATION_PATTERNS.some((pattern) => lower.includes(pattern));
  });
}

const ERROR_MESSAGE_RULES: Array<{ pattern: RegExp; message: string }> = [
  { pattern: /wallet timeout/i, message: "Your wallet didn't respond. Try again." },
  {
    pattern: /FAILED_WOULD_REVERT|execution reverted/i,
    message: 'This swap would fail on-chain. Try refreshing your quote.',
  },
  { pattern: /rate limit/i, message: 'Too many requests. Try again shortly.' },
  {
    pattern: /in-flight transaction limit/i,
    message: 'Too many pending transactions. Wait for your previous transaction to confirm.',
  },
  {
    pattern: /total cost.*exceeds the balance|insufficient funds for gas/i,
    message: 'Not enough funds to cover gas fees.',
  },
  {
    pattern: /fee cap.*lower than.*base fee|maxFeePerGas/i,
    message: 'Gas price too low for current network conditions. Try again.',
  },
  {
    pattern: /unknown connector|internal json-rpc|unexpected error/i,
    message: 'Your wallet encountered an error. Try again.',
  },
];

/**
 * Extracts a user-friendly error message from a transaction error by walking
 * the viem error cause chain and pattern-matching against known error details.
 */
export function getUserFriendlyTransactionError(err: unknown): string {
  const messages = collectErrorMessages(err);
  const combined = messages.join(' ');

  for (const rule of ERROR_MESSAGE_RULES) {
    if (rule.pattern.test(combined)) {
      return rule.message;
    }
  }

  return 'Transaction failed';
}

export function getConnectedChain(
  wallet:
    | ConnectedWallet
    | ConnectedStandardSolanaWallet
    | undefined
    | MatchaConnectedWallet<ConnectedWallet>
    | MatchaConnectedWallet<ConnectedStandardSolanaWallet>,
) {
  if (!wallet) return undefined;
  if ('chainId' in wallet) {
    const split = wallet.chainId.split(':');
    return SUPPORTED_CHAINS_BY_CHAIN_ID[Number(split[split.length - 1])];
  } else if ('standardWallet' in wallet) {
    return solana;
  }
  return undefined;
}

/**
 * Solana-first wallets expose an EVM provider that supports only a small,
 * fixed set of EVM chains and cannot add arbitrary networks. When a cross-chain
 * swap settles on an EVM chain outside that set, the funds land at the user's
 * address but the wallet cannot surface them — the user would have to import
 * the key into another wallet to access them.
 *
 * Keyed by a lowercase substring of the Privy wallet name (`wallet.meta.name`),
 * which also covers the `detected_*` and WalletConnect connection paths.
 *
 * Supported sets verified against the wallets' own docs (2026-06):
 * - Phantom: Ethereum, Polygon, Base, Monad, HyperEVM, Robinhood
 *   https://help.phantom.com/hc/en-us/articles/41372840389651-Supported-networks-chains-in-Phantom
 * - Backpack: Ethereum, Polygon, Base, Arbitrum, Optimism, Robinhood
 *   https://support.backpack.exchange/wallet/what-is-backpack-wallet
 */
export const LIMITED_EVM_WALLET_CHAIN_SUPPORT: Record<string, number[]> = {
  phantom: [
    CHAIN_IDS.MAINNET,
    CHAIN_IDS.MATIC,
    CHAIN_IDS.BASE,
    CHAIN_IDS.MONAD,
    CHAIN_IDS.HYPEREVM,
    CHAIN_IDS.ROBINHOOD,
  ],
  backpack: [
    CHAIN_IDS.MAINNET,
    CHAIN_IDS.MATIC,
    CHAIN_IDS.BASE,
    CHAIN_IDS.ARBITRUM,
    CHAIN_IDS.OPTIMISM,
    CHAIN_IDS.ROBINHOOD,
  ],
};

/**
 * Returns true when `destinationChainId` is an EVM chain the connected wallet
 * (identified by `walletName`) is known not to support, so a cross-chain swap
 * there risks the funds being inaccessible from that wallet.
 *
 * Whitelist semantics: a destination outside a known limited wallet's set
 * warns. Unknown or full-support wallets (MetaMask, Rabby, etc.) never warn —
 * they can add any EVM network, so funds stay recoverable. Solana destinations
 * are out of scope (handled by separate receiving-wallet routing). The failure
 * mode is therefore harmless over-warning, never silent under-warning.
 */
export function isDestinationChainUnsupportedByWallet({
  walletName,
  destinationChainId,
}: {
  walletName: string | undefined;
  destinationChainId: number | undefined;
}): boolean {
  if (!walletName || !destinationChainId) return false;
  if (destinationChainId === solana.id) return false;

  const normalizedName = walletName.toLowerCase();
  const matchedEntry = Object.entries(LIMITED_EVM_WALLET_CHAIN_SUPPORT).find(([walletKey]) =>
    normalizedName.includes(walletKey),
  );
  if (!matchedEntry) return false;

  const [, supportedChainIds] = matchedEntry;
  return !supportedChainIds.includes(destinationChainId);
}

/**
 * Snapshot of the Privy and wagmi state machines captured at a wallet-guard
 * failure site. The two state machines are independent: Privy holds the
 * session/wallet selection, wagmi holds the EIP-1193 provider that actually
 * signs. When they desync mid-session, the UI shows "connected" while
 * `useWalletClient` returns undefined. These fields let us identify which
 * side disagrees per Sentry event.
 */
/**
 * Where the call site got (or tried to get) its viem `WalletClient` from.
 * Sliced as a Sentry tag so we can tell whether a guard hit came from the
 * wagmi React Query hook returning undefined or the Privy-direct path
 * failing to build a provider.
 */
export type WalletClientSource = 'privy_direct' | 'wagmi_hook';

export type WalletGuardDiagnostics = {
  privy: {
    walletId: string | undefined;
    chainId: number | undefined;
  };
  wagmi: {
    status: ReturnType<typeof getAccount>['status'];
    isConnected: boolean;
    connectorId: string | undefined;
    chainId: number | undefined;
    addressMatch: boolean;
  };
  targetChainId: number | undefined;
  walletClientSource: WalletClientSource;
};

/**
 * Read both state machines without touching them. Safe to call from any
 * client-side code path; `getAccount` is a synchronous read of wagmi's store.
 */
export function captureWalletGuardDiagnostics(args: {
  ethereumWallet: ConnectedWallet | undefined;
  targetChainId: number | undefined;
  accountAddress: string | undefined;
  walletClientSource: WalletClientSource;
}): WalletGuardDiagnostics {
  const wagmiAccount = getAccount(wagmiConfig);
  return {
    privy: {
      walletId: args.ethereumWallet?.meta.id,
      chainId: getConnectedChain(args.ethereumWallet)?.id,
    },
    wagmi: {
      status: wagmiAccount.status,
      isConnected: wagmiAccount.isConnected,
      connectorId: wagmiAccount.connector?.id,
      chainId: wagmiAccount.chainId,
      addressMatch: wagmiAccount.address === args.accountAddress,
    },
    targetChainId: args.targetChainId,
    walletClientSource: args.walletClientSource,
  };
}

/**
 * Capture diagnostics and attach them to the active Sentry scope as context
 * plus a small set of searchable tags. Tags are bounded-cardinality fields
 * chosen so we can slice the issue in Sentry's tag distribution view the
 * same way we already slice `user.account.wallet`.
 */
export function reportWalletGuardDiagnostics(args: {
  ethereumWallet: ConnectedWallet | undefined;
  targetChainId: number | undefined;
  accountAddress: string | undefined;
  walletClientSource: WalletClientSource;
}): WalletGuardDiagnostics {
  const diagnostics = captureWalletGuardDiagnostics(args);
  Sentry.setContext('walletDesync', diagnostics);
  Sentry.setTag('wallet.wagmi_status', diagnostics.wagmi.status);
  Sentry.setTag('wallet.wagmi_connector_id', diagnostics.wagmi.connectorId ?? 'unknown');
  Sentry.setTag('wallet.wagmi_chain_id', diagnostics.wagmi.chainId ?? 'unknown');
  Sentry.setTag('wallet.target_chain_id', diagnostics.targetChainId ?? 'unknown');
  Sentry.setTag('wallet.privy_wallet_id', diagnostics.privy.walletId ?? 'unknown');
  const chainMismatch =
    diagnostics.wagmi.chainId !== undefined &&
    diagnostics.targetChainId !== undefined &&
    diagnostics.wagmi.chainId !== diagnostics.targetChainId;
  Sentry.setTag('wallet.chain_mismatch', chainMismatch);
  Sentry.setTag('wallet.address_match', diagnostics.wagmi.addressMatch);
  Sentry.setTag('wallet.client_source', diagnostics.walletClientSource);
  return diagnostics;
}

// Wagmi exposes the post-switch wallet client only after it observes the
// wallet's `chainChanged` event, which can lag `switchChainAsync` resolving
// by tens of milliseconds. Five tries at 100 ms covers the window we have
// observed in production without blocking the user noticeably.
export const WALLET_CLIENT_READ_ATTEMPTS_AFTER_SWITCH = 5;
export const WALLET_CLIENT_READ_DELAY_MS = 100;

type FetchedWalletClient = Awaited<ReturnType<typeof getWalletClient>>;

/**
 * Read the wagmi wallet client for `chainId`, retrying briefly to ride out
 * the post-`switchChainAsync` window where the connector has not yet observed
 * the wallet's `chainChanged` event. Returns `undefined` after all attempts.
 */
export async function getWalletClientAfterChainSwitch(
  chainId: number,
): Promise<FetchedWalletClient | undefined> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= WALLET_CLIENT_READ_ATTEMPTS_AFTER_SWITCH; attempt++) {
    try {
      const client = await getWalletClient(wagmiConfig, { chainId });
      if (client) return client;
    } catch (error: unknown) {
      // ConnectorChainMismatchError etc. — wagmi may catch up on retry.
      lastError = error;
    }
    if (attempt < WALLET_CLIENT_READ_ATTEMPTS_AFTER_SWITCH) {
      await sleep(WALLET_CLIENT_READ_DELAY_MS);
    }
  }
  logger.warn('Failed to read wagmi wallet client after chain switch', {
    chainId,
    attempts: WALLET_CLIENT_READ_ATTEMPTS_AFTER_SWITCH,
    lastError,
  });
  return undefined;
}

/**
 * Build a viem `WalletClient` from a Privy `ConnectedWallet` by reading
 * its EIP-1193 provider. See
 * https://docs.privy.io/wallets/connectors/ethereum/integrations/viem.
 *
 * Prefer this over wagmi's `useWalletClient` hook at signing sites. The
 * hook is React Query backed and returns `data: undefined` while pending,
 * which happens on mount, when the chainId key changes, and when wagmi
 * invalidates the query (MetaMask re-emits `chainChanged` on tab focus,
 * for example). The wallet is connected the whole time; the hook just
 * hasn't caught up. Reading the provider directly skips that cache and
 * also drops the Privy/wagmi reconciliation step.
 *
 * After `wallet.switchChain(...)` the existing provider instance is not
 * updated, so call this helper fresh after any chain switch instead of
 * caching the result.
 *
 * Returns `undefined` when there is no wallet or when `getEthereumProvider`
 * throws (a real connection failure, logged as a warning).
 */
export async function getPrivyWalletClient(
  wallet: ConnectedWallet | undefined,
  chain: Chain,
): Promise<WalletClient | undefined> {
  if (!wallet) return undefined;
  try {
    const provider = await wallet.getEthereumProvider();
    return createWalletClient({
      account: wallet.address as Address,
      chain,
      transport: custom(provider),
    });
  } catch (error) {
    logger.warn('Failed to build viem WalletClient from Privy wallet', {
      walletId: wallet.meta.id,
      chainId: chain.id,
      error,
    });
    return undefined;
  }
}

interface SwitchChainOptions {
  chainId: number;
  switchChainAsync: SwitchChainMutateAsync<Config>;
}

export type SwitchChainResult =
  | { success: true }
  | { success: false; reason: 'user_rejected' | 'unsupported' | 'error' };

/**
 * Attempts to switch the user's wallet to the specified chain.
 * Handles common errors like user rejection and unsupported chain switching.
 *
 * @returns An object indicating success or failure with a reason
 */
export async function trySwitchChain({
  chainId,
  switchChainAsync,
}: SwitchChainOptions): Promise<SwitchChainResult> {
  try {
    await switchChainAsync({ chainId });
    return { success: true };
  } catch (e: unknown) {
    if (e instanceof Error) {
      if (e instanceof UserRejectedRequestError) {
        return { success: false, reason: 'user_rejected' };
      } else if (e instanceof SwitchChainNotSupportedError || e instanceof SwitchChainError) {
        logger.warn('SwitchChainError', e.details);

        const isAlreadyPending = e.details.startsWith(
          `Request of type 'wallet_switchEthereumChain' already pending`,
        );
        // viem throws this when the wallet acknowledges `wallet_switchEthereumChain`
        // but the post-switch chain id readback still returns the old chain.
        // Observed on Phantom mobile, MetaMask mobile, Frame — wallet/environmental,
        // not actionable from our side.
        const isProviderNotConnected = e.details.includes('not connected to the requested chain');

        if (!isAlreadyPending) {
          toast(`Please switch to ${NETWORK_NAME_PER_CHAIN_ID[chainId]} from your wallet.`);
        }

        if (!isProviderNotConnected) {
          trackError(e);
        }
        return { success: false, reason: 'unsupported' };
      } else {
        logger.warn('SwitchChainError', e);
        trackError(e);
      }
    }
    return { success: false, reason: 'error' };
  }
}
